import { FAVOURITE_FILTERS } from "../utils/constants";
import { storeGet } from '@/store/store-utils';

const initialState = () => ({
  list: [],
  isListLoading: false,
  isSaving: false,
  isDeleting: false,
  deleteTargetId: null,
});

const findFilterId = (filter) => filter?._id || filter?.id || filter?.filter_id || null;

export default {
  namespaced: true,
  state: initialState(),

  mutations: {
    SET_FAVOURITE_FILTERS(state, filters) {
      state.list = Array.isArray(filters) ? filters : [];
    },
    UPSERT_FAVOURITE_FILTERS(state, filters) {
      const incoming = Array.isArray(filters) ? filters : filters ? [filters] : [];
      if (!incoming.length) {
        return;
      }
      const mapped = [...state.list];
      incoming.forEach((filter) => {
        const filterId = findFilterId(filter);
        if (!filterId) {
          return;
        }
        const existingIndex = mapped.findIndex((item) => findFilterId(item) === filterId);
        if (existingIndex > -1) {
          mapped.splice(existingIndex, 1, filter);
        } else {
          mapped.unshift(filter);
        }
      });
      state.list = mapped;
    },
    REMOVE_FAVOURITE_FILTER(state, filterId) {
      state.list = state.list.filter((item) => findFilterId(item) !== filterId);
    },
    SET_LIST_LOADING(state, status) {
      state.isListLoading = Boolean(status);
    },
    SET_SAVING(state, status) {
      state.isSaving = Boolean(status);
    },
    SET_DELETING(state, { status, targetId }) {
      state.isDeleting = Boolean(status);
      state.deleteTargetId = status ? targetId : null;
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
  },

  actions: {
    async fetchFavouriteFilters({ commit, rootState }, { axiosInstance, notify = true } = {}) {
      if (!axiosInstance) {
        throw new Error("Missing axios instance.");
      }
      const clientId = rootState.client;
      if (!clientId) {
        commit("sbError", FAVOURITE_FILTERS.MESSAGES.CLIENT_REQUIRED, { root: true });
        return false;
      }

      commit("SET_LIST_LOADING", true);
      try {
        const response = await storeGet(axiosInstance, rootState, FAVOURITE_FILTERS.API_ENDPOINTS.BASE);

        if (response?.data?.status !== "success") {
          throw new Error(response?.data?.message || FAVOURITE_FILTERS.MESSAGES.FETCH_ERROR);
        }

        commit("SET_FAVOURITE_FILTERS", response?.data?.data || []);
        if (notify) {
          commit("sbSuccess", FAVOURITE_FILTERS.MESSAGES.FETCH_SUCCESS, { root: true });
        }
        return true;
      } catch (error) {
        const message = error?.message || FAVOURITE_FILTERS.MESSAGES.FETCH_ERROR;
        commit("sbError", message, { root: true });
        throw error;
      } finally {
        commit("SET_LIST_LOADING", false);
      }
    },

    async createFavouriteFilter({ commit, dispatch, rootState }, { axiosInstance, payload }) {
      if (!axiosInstance) {
        throw new Error("Missing axios instance.");
      }
      const clientId = rootState.client;
      if (!clientId) {
        commit("sbError", FAVOURITE_FILTERS.MESSAGES.CLIENT_REQUIRED, { root: true });
        return false;
      }
      if (!payload || !payload.title) {
        throw new Error("Favourite filter title is required.");
      }

      commit("SET_SAVING", true);
      try {
        const requestBody = { ...payload, client: clientId };
        const response = await axiosInstance.post(FAVOURITE_FILTERS.API_ENDPOINTS.BASE_CREATE, requestBody);

        if (response?.data?.status !== "success") {
          throw new Error(response?.data?.message || FAVOURITE_FILTERS.MESSAGES.CREATE_ERROR);
        }

        // Re-fetch the full list from backend to get correct status values.
        // Treat refresh as best-effort so that a transient fetch error does not
        // turn a successful create into a reported failure.
        try {
          await dispatch("fetchFavouriteFilters", { axiosInstance, notify: false });
        } catch (refreshError) {
          console.error("[FavouriteFiltersStore]: createFavouriteFilter refresh error", refreshError);
        }
        commit("sbSuccess", FAVOURITE_FILTERS.MESSAGES.CREATE_SUCCESS, { root: true });
        return true;
      } catch (error) {
        const message = error?.message || FAVOURITE_FILTERS.MESSAGES.CREATE_ERROR;
        commit("sbError", message, { root: true });
        throw error;
      } finally {
        commit("SET_SAVING", false);
      }
    },

    async deleteFavouriteFilter({ commit, rootState }, { axiosInstance, filterId }) {
      if (!axiosInstance) {
        throw new Error("Missing axios instance.");
      }
      const clientId = rootState.client;
      if (!clientId) {
        commit("sbError", FAVOURITE_FILTERS.MESSAGES.CLIENT_REQUIRED, { root: true });
        return false;
      }
      if (!filterId) {
        throw new Error("Favourite filter id is required.");
      }

      commit("SET_DELETING", { status: true, targetId: filterId });
      try {
        const response = await axiosInstance.delete(
          `${FAVOURITE_FILTERS.API_ENDPOINTS.BASE_DELETE}/${filterId}`,
          {
            data: { client: clientId },
          }
        );

        if (response?.data?.status !== "success") {
          throw new Error(response?.data?.message || FAVOURITE_FILTERS.MESSAGES.DELETE_ERROR);
        }

        commit("REMOVE_FAVOURITE_FILTER", filterId);
        commit("sbSuccess", FAVOURITE_FILTERS.MESSAGES.DELETE_SUCCESS, { root: true });
        return true;
      } catch (error) {
        const message = error?.message || FAVOURITE_FILTERS.MESSAGES.DELETE_ERROR;
        commit("sbError", message, { root: true });
        throw error;
      } finally {
        commit("SET_DELETING", { status: false, targetId: null });
      }
    },
  },

  getters: {
    favouriteFilters: (state) => state.list,
    isFavouriteFiltersLoading: (state) => state.isListLoading,
    isFavouriteFiltersSaving: (state) => state.isSaving,
    isFavouriteFiltersDeleting: (state) => state.isDeleting,
    favouriteFilterDeleteTarget: (state) => state.deleteTargetId,
  },
};
